import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Campaign,
  Close,
  ContentCopy,
  DataObject,
  DeleteOutline,
  Event,
  ExpandMore,
  HelpOutline,
  Hub,
  Inventory2,
  PhoneInTalk,
  Person,
  PlayArrow,
  Psychology,
  Refresh,
  Save,
  UploadFile,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';

import api from '../services/api';
import {
  fetchContactZConnections,
  fetchContactZIntelligencePersonas,
  fetchContactZOfficialTemplates,
  getContactZConnectionState,
  syncContactZOfficialTemplates,
} from '../services/contactZ';
import { usePermissions } from '../contexts/PermissionContext';
import usePageTitle from '../hooks/usePageTitle';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { useConfirmDialog } from '../components/ConfirmDialog';
import {
  CONTACT_Z_MEDIA_LIMITS_BYTES,
  SCRIPT_ASSET_TECHNICAL_MAX_BYTES,
  contactZMessageTypeRequiresAsset,
  contactZMessageTypeSupportsCaption,
  contactZMessageTypeSupportsTextTemplate,
  formatContactZByteLimit,
  getContactZTemplateParameterSlots,
  getContactZTemplateParameterValue,
  setContactZTemplateParameterValue,
} from '../utils/contactZ';
import type { ContactZTemplateParameterSlot } from '../utils/contactZ';

import type {
  ContactZConnection,
  ContactZAIHandoffConfig,
  ContactZIntelligencePersona,
  ContactZMessageConfig,
  ContactZMessageType,
  ContactZOfficialTemplate,
  PreCallFailurePolicy,
  PreCallMode,
  ScriptAsset,
  ScriptType,
} from '../types';

const SCRIPT_LANGUAGE = 'python';

const defaultCode = `# =============================================================================
# Script de Automação (Python)
# =============================================================================
#
# Objetos disponíveis no runtime:
#
# campaign = CampaignContext(
#   "id": "uuid",                   # ID da campanha
#   "name": "Nome da Campanha",     # Nome
#   "status": "active",             # Status atual
#   "total_contacts": 100,          # Total de contatos
#   "connected_calls": 50,          # Chamadas conectadas
#   "failed_calls": 10              # Chamadas falhadas
# )
#
# call = CallContext(               # Presente em eventos de chamada
#   "id": "uuid",
#   "to_number": "+5511999999999",
#   "status": "completed",
#   "duration": 120,
#   "is_converted": True,
#   "is_optout": False,
#   "transcript_full": [...]
# }
#
# contact = ContactContext(         # Presente quando há contato
#   "id": "uuid",
#   "display_name": "João Silva",
#   "phone": "+5511999999999",
#   "status": "reached",
#   "attempt_count": 2
# )
#
# event = EventContext(
#   "type": "call_completed",
#   "timestamp": "2025-..."
# )
#
# assets = [AssetContext(...)]
# assets_by_name = {"manual_principal": AssetContext(...)}
# assets_by_filename = {"manual.pdf": AssetContext(...)}
# assets_by_id = {"<public_id>": AssetContext(...)}
#
# Exemplo simples:
campaign_name = campaign.name if campaign else "N/A"
event_type = event.type if event else "manual"
print(f"[script] evento={event_type} campanha={campaign_name}")
`;

const PRE_CALL_DEFAULT_CODE = `# Return exactly one admission result before an outbound call is created.
# The runtime exposes campaign, contact, phone and previous_pre_call. There is no call.
set_pre_call_result({
    "schema_version": 1,
    "decision": "proceed",
    "reason_code": "eligible",
    "valid_for_seconds": 60,
    "variables": {
        "precall__segment": "standard",
    },
})
`;

const PRE_CALL_DEFAULT_TEST_INPUT = JSON.stringify({
  event: {
    type: 'pre_call',
    timestamp: '2026-01-01T12:00:00Z',
    execution_id: 'manual-test',
  },
  campaign: {
    id: 'campaign-test',
    name: 'Test campaign',
    status: 'active',
  },
  contact: {
    id: 'contact-test',
    display_name: 'Test contact',
    status: 'pending',
    attempt_count: 0,
    variables: {
      customer_reference: 'test-reference',
    },
  },
  phone: {
    id: 'phone-test',
    value: '+5511999999999',
    position: 0,
  },
  previous_pre_call: {
    attempt_count: 0,
    reason_code: null,
  },
  assets: [],
}, null, 2);

function buildDefaultContactZConfig(): ContactZMessageConfig {
  return {
    connectionId: '',
    messageType: 'text',
    targetPhonePath: 'contact.phone',
    fallbackToCallToNumber: true,
    textTemplate: '',
    captionTemplate: '',
    assetId: null,
    templateName: '',
    templateLanguage: 'pt_BR',
    templateComponents: [],
  };
}

function buildDefaultContactZAIHandoffConfig(): ContactZAIHandoffConfig {
  return {
    connectionId: '',
    targetPhonePath: 'contact.phone',
    fallbackToCallToNumber: true,
    intelligencePersonaUuid: '',
    forceAssignToAi: false,
    instructionsTemplate: '',
    initialMessage: {
      mode: 'ai_generated',
      text: '',
      templateName: '',
      templateLanguage: 'pt_BR',
      templateComponents: [],
      nicknameMode: 'none',
    },
  };
}

function isContactZHandoffConnectionEligible(connection: ContactZConnection): boolean {
  return connection.remoteIsEnabled
    && connection.operationMode === 'default'
    && ['whatsapp_business', 'whatsapp_cloud'].includes(connection.platform);
}

function resolveScriptErrorMessage(error: unknown, fallback: string): string {
  const maybeAxiosError = error as {
    response?: { data?: Record<string, unknown> };
  };
  const responseData = maybeAxiosError?.response?.data;
  if (!responseData) {
    return fallback;
  }

  const directMessage = responseData.detail || responseData.error;
  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage;
  }

  const firstEntry = Object.values(responseData)[0];
  if (typeof firstEntry === 'string' && firstEntry.trim()) {
    return firstEntry;
  }
  if (Array.isArray(firstEntry) && typeof firstEntry[0] === 'string') {
    return firstEntry[0];
  }

  return fallback;
}

export default function ScriptCreatePage() {
  const { t } = useTranslation(['pages', 'common']);
  const { formatNumber } = useNumberFormat();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { confirm, DialogComponent: ConfirmDialogComponent } = useConfirmDialog();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState('call_completed');
  const [scriptType, setScriptType] = useState<ScriptType>('code');
  const [code, setCode] = useState(defaultCode);
  const [delaySeconds, setDelaySeconds] = useState(0);
  const [timeoutSeconds, setTimeoutSeconds] = useState(30);
  const [preCallMode, setPreCallMode] = useState<PreCallMode>('enrichment_only');
  const [preCallFailurePolicy, setPreCallFailurePolicy] = useState<PreCallFailurePolicy>('proceed');
  const [preCallMaxAttempts, setPreCallMaxAttempts] = useState(3);
  const [preCallInitialRetrySeconds, setPreCallInitialRetrySeconds] = useState(30);
  const [preCallMaxRetrySeconds, setPreCallMaxRetrySeconds] = useState(900);
  const [preCallBreakerFailureThreshold, setPreCallBreakerFailureThreshold] = useState(3);
  const [preCallBreakerOpenSeconds, setPreCallBreakerOpenSeconds] = useState(60);
  const [preCallMaxFreshnessSeconds, setPreCallMaxFreshnessSeconds] = useState(60);
  const [preCallMaxEnrichmentVariables, setPreCallMaxEnrichmentVariables] = useState(8);
  const [preCallMaxEnrichmentValueBytes, setPreCallMaxEnrichmentValueBytes] = useState(512);
  const [preCallTestInput, setPreCallTestInput] = useState(PRE_CALL_DEFAULT_TEST_INPUT);
  const [preCallTestOutput, setPreCallTestOutput] = useState('');
  const [preCallTestResult, setPreCallTestResult] = useState('');
  const [preCallTestError, setPreCallTestError] = useState('');
  const [preCallTestLoading, setPreCallTestLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<ScriptAsset[]>([]);
  const [assetName, setAssetName] = useState('');
  const [assetFile, setAssetFile] = useState<File | null>(null);
  const [assetError, setAssetError] = useState('');
  const [assetSuccess, setAssetSuccess] = useState('');
  const [assetLoading, setAssetLoading] = useState(false);
  const [documentationDrawer, setDocumentationDrawer] = useState<'variables' | 'libraries' | null>(null);
  const [expandedVariableSections, setExpandedVariableSections] = useState<Record<string, boolean>>({
    campaign: true,
    call: true,
    contact: true,
    event: true,
    phone: true,
    previous_pre_call: true,
    assets: true,
  });
  const [contactZConnections, setContactZConnections] = useState<ContactZConnection[]>([]);
  const [contactZConnectionsLoading, setContactZConnectionsLoading] = useState(false);
  const [contactZConnectionsError, setContactZConnectionsError] = useState('');
  const [contactZMessageConfig, setContactZMessageConfig] = useState<ContactZMessageConfig>(
    buildDefaultContactZConfig(),
  );
  const [contactZAIHandoffConfig, setContactZAIHandoffConfig] = useState<ContactZAIHandoffConfig>(
    buildDefaultContactZAIHandoffConfig(),
  );
  const [contactZPersonas, setContactZPersonas] = useState<ContactZIntelligencePersona[]>([]);
  const [contactZPersonasLoading, setContactZPersonasLoading] = useState(false);
  const [contactZPersonasError, setContactZPersonasError] = useState('');
  const [contactZOfficialTemplates, setContactZOfficialTemplates] = useState<ContactZOfficialTemplate[]>([]);
  const [contactZOfficialTemplatesLoading, setContactZOfficialTemplatesLoading] = useState(false);
  const [contactZOfficialTemplatesSyncing, setContactZOfficialTemplatesSyncing] = useState(false);
  const [contactZOfficialTemplatesError, setContactZOfficialTemplatesError] = useState('');
  const [contactZHandoffProvidedType, setContactZHandoffProvidedType] = useState<'text' | 'template'>('text');

  const canAccessContactZ = hasPermission('contact_z_access');
  const isContactZMessageScript = scriptType === 'contact_z_message';
  const isContactZAIHandoffScript = scriptType === 'contact_z_ai_handoff';
  const isPreCallScript = eventType === 'pre_call';
  const selectedContactZMessageConnection = useMemo(
    () => contactZConnections.find((connection) => connection.id === contactZMessageConfig.connectionId) || null,
    [contactZConnections, contactZMessageConfig.connectionId],
  );
  const selectedContactZAIHandoffConnection = useMemo(
    () => contactZConnections.find((connection) => connection.id === contactZAIHandoffConfig.connectionId) || null,
    [contactZConnections, contactZAIHandoffConfig.connectionId],
  );
  const selectedContactZPersona = useMemo(
    () => contactZPersonas.find((persona) => persona.id === contactZAIHandoffConfig.intelligencePersonaUuid) || null,
    [contactZAIHandoffConfig.intelligencePersonaUuid, contactZPersonas],
  );
  const selectedTemplateName = isContactZAIHandoffScript
    ? contactZAIHandoffConfig.initialMessage.templateName
    : contactZMessageConfig.templateName;
  const selectedTemplateLanguage = isContactZAIHandoffScript
    ? contactZAIHandoffConfig.initialMessage.templateLanguage
    : contactZMessageConfig.templateLanguage;
  const selectedContactZOfficialTemplate = useMemo(
    () => contactZOfficialTemplates.find((template) => (
      template.name === selectedTemplateName
      && template.language === selectedTemplateLanguage
    )) || null,
    [contactZOfficialTemplates, selectedTemplateLanguage, selectedTemplateName],
  );
  const contactZTemplateParameterSlots = useMemo(
    () => getContactZTemplateParameterSlots(selectedContactZOfficialTemplate),
    [selectedContactZOfficialTemplate],
  );
  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === contactZMessageConfig.assetId) || null,
    [assets, contactZMessageConfig.assetId],
  );
  const requiresAsset = isContactZMessageScript && contactZMessageTypeRequiresAsset(contactZMessageConfig.messageType);
  const supportsCaption = isContactZMessageScript && contactZMessageTypeSupportsCaption(contactZMessageConfig.messageType);
  const supportsTextTemplate = isContactZMessageScript && contactZMessageTypeSupportsTextTemplate(contactZMessageConfig.messageType);
  const messageLimitLabel = !isContactZMessageScript || ['text', 'official_template'].includes(contactZMessageConfig.messageType)
    ? null
    : formatContactZByteLimit(
      CONTACT_Z_MEDIA_LIMITS_BYTES[
        contactZMessageConfig.messageType as keyof typeof CONTACT_Z_MEDIA_LIMITS_BYTES
      ],
    );

  usePageTitle(t('pages:scripts.title'));

  const eventTypes = [
    { value: 'call_started', label: t('pages:scripts.eventTypes.call_started') },
    { value: 'call_completed', label: t('pages:scripts.eventTypes.call_completed') },
    { value: 'call_finalized', label: t('pages:scripts.eventTypes.call_finalized') },
    { value: 'call_failed', label: t('pages:scripts.eventTypes.call_failed') },
    { value: 'call_converted', label: t('pages:scripts.eventTypes.call_converted') },
    { value: 'call_optout', label: t('pages:scripts.eventTypes.call_optout') },
    { value: 'campaign_started', label: t('pages:scripts.eventTypes.campaign_started') },
    { value: 'campaign_paused', label: t('pages:scripts.eventTypes.campaign_paused') },
    { value: 'campaign_cancelled', label: t('pages:scripts.eventTypes.campaign_cancelled') },
    { value: 'campaign_completed', label: t('pages:scripts.eventTypes.campaign_completed') },
    { value: 'contact_converted', label: t('pages:scripts.eventTypes.contact_converted') },
    { value: 'contact_optout', label: t('pages:scripts.eventTypes.contact_optout') },
    { value: 'contact_mismatch', label: t('pages:scripts.eventTypes.contact_mismatch') },
    { value: 'pre_call', label: t('pages:scripts.eventTypes.pre_call') },
    { value: 'manual', label: t('pages:scripts.eventTypes.manual') },
  ];

  const payloadSections = [
    {
      key: 'campaign',
      title: t('pages:scripts.availableVariables.sections.campaign.title'),
      icon: <Campaign fontSize="small" color="primary" />,
      fields: [
        { path: 'campaign.id', description: t('pages:scripts.availableVariables.sections.campaign.fields.id') },
        { path: 'campaign.name', description: t('pages:scripts.availableVariables.sections.campaign.fields.name') },
        { path: 'campaign.status', description: t('pages:scripts.availableVariables.sections.campaign.fields.status') },
        { path: 'campaign.total_contacts', description: t('pages:scripts.availableVariables.sections.campaign.fields.total_contacts') },
        { path: 'campaign.connected_calls', description: t('pages:scripts.availableVariables.sections.campaign.fields.connected_calls') },
        { path: 'campaign.conversion_eligible_calls', description: t('pages:scripts.availableVariables.sections.campaign.fields.conversion_eligible_calls') },
        { path: 'campaign.failed_calls', description: t('pages:scripts.availableVariables.sections.campaign.fields.failed_calls') },
      ],
    },
    {
      key: 'call',
      title: t('pages:scripts.availableVariables.sections.call.title'),
      icon: <PhoneInTalk fontSize="small" color="primary" />,
      fields: [
        { path: 'call.id', description: t('pages:scripts.availableVariables.sections.call.fields.id') },
        { path: 'call.direction', description: t('pages:scripts.availableVariables.sections.call.fields.direction') },
        { path: 'call.origin', description: t('pages:scripts.availableVariables.sections.call.fields.origin') },
        { path: 'call.to_number', description: t('pages:scripts.availableVariables.sections.call.fields.to_number') },
        { path: 'call.status', description: t('pages:scripts.availableVariables.sections.call.fields.status') },
        { path: 'call.duration', description: t('pages:scripts.availableVariables.sections.call.fields.duration') },
        { path: 'call.duration_formatted', description: t('pages:scripts.availableVariables.sections.call.fields.duration_formatted') },
        { path: 'call.billed_seconds', description: t('pages:scripts.availableVariables.sections.call.fields.billed_seconds') },
        { path: 'call.ring_duration', description: t('pages:scripts.availableVariables.sections.call.fields.ring_duration') },
        { path: 'call.recording_url', description: t('pages:scripts.availableVariables.sections.call.fields.recording_url') },
        { path: 'call.recording_base64', description: t('pages:scripts.availableVariables.sections.call.fields.recording_base64') },
        { path: 'call.recording_file', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file') },
        { path: 'call.recording_file.filename', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file_filename') },
        { path: 'call.recording_file.mime_type', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file_mime_type') },
        { path: 'call.recording_file.content_base64', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file_content_base64') },
        { path: 'call.recording_file.to_bytes()', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file_to_bytes') },
        { path: 'call.recording_file.to_file_tuple()', description: t('pages:scripts.availableVariables.sections.call.fields.recording_file_to_file_tuple') },
        { path: 'call.transcript_full', description: t('pages:scripts.availableVariables.sections.call.fields.transcript_full') },
        { path: 'call.transcript_full[].role', description: t('pages:scripts.availableVariables.sections.call.fields.transcript_full_role') },
        { path: 'call.transcript_full[].message', description: t('pages:scripts.availableVariables.sections.call.fields.transcript_full_message') },
        { path: 'call.transcript_full[].time_in_call_secs', description: t('pages:scripts.availableVariables.sections.call.fields.transcript_full_time_in_call_secs') },
        { path: 'call.conversation_summary', description: t('pages:scripts.availableVariables.sections.call.fields.conversation_summary') },
        { path: 'call.data_collected', description: t('pages:scripts.availableVariables.sections.call.fields.data_collected') },
        { path: 'call.data_collected.<campo>.value', description: t('pages:scripts.availableVariables.sections.call.fields.data_collected_value') },
        { path: 'call.data_collected.<campo>.rationale', description: t('pages:scripts.availableVariables.sections.call.fields.data_collected_rationale') },
        { path: 'call.is_converted', description: t('pages:scripts.availableVariables.sections.call.fields.is_converted') },
        { path: 'call.is_optout', description: t('pages:scripts.availableVariables.sections.call.fields.is_optout') },
        { path: 'call.is_mismatch', description: t('pages:scripts.availableVariables.sections.call.fields.is_mismatch') },
        { path: 'call.conversion_criteria', description: t('pages:scripts.availableVariables.sections.call.fields.conversion_criteria') },
        { path: 'call.scheduled_at', description: t('pages:scripts.availableVariables.sections.call.fields.scheduled_at') },
        { path: 'call.queued_at', description: t('pages:scripts.availableVariables.sections.call.fields.queued_at') },
        { path: 'call.ringing_at', description: t('pages:scripts.availableVariables.sections.call.fields.ringing_at') },
        { path: 'call.initiated_at', description: t('pages:scripts.availableVariables.sections.call.fields.initiated_at') },
        { path: 'call.answered_at', description: t('pages:scripts.availableVariables.sections.call.fields.answered_at') },
        { path: 'call.ended_at', description: t('pages:scripts.availableVariables.sections.call.fields.ended_at') },
        { path: 'call.created_at', description: t('pages:scripts.availableVariables.sections.call.fields.created_at') },
        { path: 'call.updated_at', description: t('pages:scripts.availableVariables.sections.call.fields.updated_at') },
        { path: 'call.campaign_id', description: t('pages:scripts.availableVariables.sections.call.fields.campaign_id') },
        { path: 'call.campaign_name', description: t('pages:scripts.availableVariables.sections.call.fields.campaign_name') },
        { path: 'call.campaign_contact_display_name', description: t('pages:scripts.availableVariables.sections.call.fields.campaign_contact_display_name') },
        { path: 'call.variables', description: t('pages:scripts.availableVariables.sections.call.fields.variables') },
        { path: 'call.agent_name', description: t('pages:scripts.availableVariables.sections.call.fields.agent_name') },
        { path: 'call.origin_line_name', description: t('pages:scripts.availableVariables.sections.call.fields.origin_line_name') },
        { path: 'call.phone_label', description: t('pages:scripts.availableVariables.sections.call.fields.phone_label') },
      ],
    },
    {
      key: 'contact',
      title: t('pages:scripts.availableVariables.sections.contact.title'),
      icon: <Person fontSize="small" color="primary" />,
      fields: [
        { path: 'contact.id', description: t('pages:scripts.availableVariables.sections.contact.fields.id') },
        { path: 'contact.display_name', description: t('pages:scripts.availableVariables.sections.contact.fields.display_name') },
        { path: 'contact.phone', description: t('pages:scripts.availableVariables.sections.contact.fields.phone') },
        { path: 'contact.status', description: t('pages:scripts.availableVariables.sections.contact.fields.status') },
        { path: 'contact.attempt_count', description: t('pages:scripts.availableVariables.sections.contact.fields.attempt_count') },
        { path: 'contact.is_converted', description: t('pages:scripts.availableVariables.sections.contact.fields.is_converted') },
        { path: 'contact.is_optout', description: t('pages:scripts.availableVariables.sections.contact.fields.is_optout') },
      ],
    },
    {
      key: 'event',
      title: t('pages:scripts.availableVariables.sections.event.title'),
      icon: <Event fontSize="small" color="primary" />,
      fields: [
        { path: 'event.type', description: t('pages:scripts.availableVariables.sections.event.fields.type') },
        { path: 'event.timestamp', description: t('pages:scripts.availableVariables.sections.event.fields.timestamp') },
      ],
    },
    {
      key: 'assets',
      title: t('pages:scripts.availableVariables.sections.assets.title'),
      icon: <Inventory2 fontSize="small" color="primary" />,
      fields: [
        { path: 'assets', description: t('pages:scripts.availableVariables.sections.assets.fields.assets') },
        { path: 'assets[].id', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_id') },
        { path: 'assets[].file_id', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_id') },
        { path: 'assets[].name', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_name') },
        { path: 'assets[].filename', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_filename') },
        { path: 'assets[].mime_type', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_mime_type') },
        { path: 'assets[].size_bytes', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_size_bytes') },
        { path: 'assets[].url', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_url') },
        { path: 'assets[].base64', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_base64') },
        { path: 'assets[].file', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file') },
        { path: 'assets[].file.filename', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_filename') },
        { path: 'assets[].file.mime_type', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_mime_type') },
        { path: 'assets[].file.content_base64', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_content_base64') },
        { path: 'assets[].file.to_bytes()', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_to_bytes') },
        { path: 'assets[].file.to_file_tuple()', description: t('pages:scripts.availableVariables.sections.assets.fields.asset_file_to_file_tuple') },
        { path: 'assets_by_name', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_name') },
        { path: 'assets_by_filename', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_filename') },
        { path: 'assets_by_id', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_id') },
        { path: 'assets_base64', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_base64') },
        { path: 'assets_file', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_file') },
        { path: 'assets_by_name_base64', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_name_base64') },
        { path: 'assets_by_name_file', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_name_file') },
        { path: 'assets_by_filename_base64', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_filename_base64') },
        { path: 'assets_by_filename_file', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_filename_file') },
        { path: 'assets_by_id_base64', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_id_base64') },
        { path: 'assets_by_id_file', description: t('pages:scripts.availableVariables.sections.assets.fields.assets_by_id_file') },
      ],
    },
  ];
  const preCallPayloadSections = [
    {
      key: 'campaign',
      title: t('pages:scripts.availableVariables.sections.campaign.title'),
      icon: <Campaign fontSize="small" color="primary" />,
      fields: [
        { path: 'campaign.id', description: t('pages:scripts.availableVariables.sections.campaign.fields.id') },
        { path: 'campaign.name', description: t('pages:scripts.availableVariables.sections.campaign.fields.name') },
        { path: 'campaign.status', description: t('pages:scripts.availableVariables.sections.campaign.fields.status') },
      ],
    },
    {
      key: 'contact',
      title: t('pages:scripts.availableVariables.sections.contact.title'),
      icon: <Person fontSize="small" color="primary" />,
      fields: [
        { path: 'contact.id', description: t('pages:scripts.availableVariables.sections.contact.fields.id') },
        { path: 'contact.display_name', description: t('pages:scripts.availableVariables.sections.contact.fields.display_name') },
        { path: 'contact.status', description: t('pages:scripts.availableVariables.sections.contact.fields.status') },
        { path: 'contact.attempt_count', description: t('pages:scripts.availableVariables.sections.contact.fields.attempt_count') },
        { path: 'contact.variables', description: t('pages:scripts.availableVariables.sections.contact.fields.variables') },
      ],
    },
    {
      key: 'phone',
      title: t('pages:scripts.availableVariables.sections.phone.title'),
      icon: <PhoneInTalk fontSize="small" color="primary" />,
      fields: [
        { path: 'phone.id', description: t('pages:scripts.availableVariables.sections.phone.fields.id') },
        { path: 'phone.value', description: t('pages:scripts.availableVariables.sections.phone.fields.value') },
        { path: 'phone.position', description: t('pages:scripts.availableVariables.sections.phone.fields.position') },
      ],
    },
    {
      key: 'previous_pre_call',
      title: t('pages:scripts.availableVariables.sections.previousPreCall.title'),
      icon: <Event fontSize="small" color="primary" />,
      fields: [
        { path: 'previous_pre_call.attempt_count', description: t('pages:scripts.availableVariables.sections.previousPreCall.fields.attempt_count') },
        { path: 'previous_pre_call.reason_code', description: t('pages:scripts.availableVariables.sections.previousPreCall.fields.reason_code') },
      ],
    },
    {
      key: 'event',
      title: t('pages:scripts.availableVariables.sections.event.title'),
      icon: <Event fontSize="small" color="primary" />,
      fields: [
        { path: 'event.type', description: t('pages:scripts.availableVariables.sections.event.fields.type') },
        { path: 'event.timestamp', description: t('pages:scripts.availableVariables.sections.event.fields.timestamp') },
        { path: 'event.execution_id', description: t('pages:scripts.availableVariables.sections.event.fields.execution_id') },
      ],
    },
  ];
  const visiblePayloadSections = isPreCallScript
    ? preCallPayloadSections
    : payloadSections;

  const availableLibraries = [
    { name: 'base64', description: t('pages:scripts.availableLibraries.items.base64') },
    { name: 'hashlib', description: t('pages:scripts.availableLibraries.items.hashlib') },
    { name: 'requests', description: t('pages:scripts.availableLibraries.items.requests') },
    { name: 'json', description: t('pages:scripts.availableLibraries.items.json') },
    { name: 're', description: t('pages:scripts.availableLibraries.items.re') },
    { name: 'math', description: t('pages:scripts.availableLibraries.items.math') },
    { name: 'datetime', description: t('pages:scripts.availableLibraries.items.datetime') },
    { name: 'time', description: t('pages:scripts.availableLibraries.items.time') },
    { name: 'decimal', description: t('pages:scripts.availableLibraries.items.decimal') },
    { name: 'uuid', description: t('pages:scripts.availableLibraries.items.uuid') },
    { name: 'typing', description: t('pages:scripts.availableLibraries.items.typing') },
    { name: 'itertools', description: t('pages:scripts.availableLibraries.items.itertools') },
    { name: 'functools', description: t('pages:scripts.availableLibraries.items.functools') },
    { name: 'collections', description: t('pages:scripts.availableLibraries.items.collections') },
    { name: 'pprint', description: t('pages:scripts.availableLibraries.items.pprint') },
    { name: 'unicodedata', description: t('pages:scripts.availableLibraries.items.unicodedata') },
  ];

  const fetchScriptRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchAssetsRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchConnectionsRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchPersonasRef = useRef<(connectionId: string) => Promise<void>>(
    async () => undefined,
  );
  const fetchOfficialTemplatesRef = useRef<(connectionId: string) => Promise<void>>(
    async () => undefined,
  );

  useEffect(() => {
    if (isEditing) {
      void fetchScriptRef.current();
      void fetchAssetsRef.current();
    }
    if (canAccessContactZ) {
      void fetchConnectionsRef.current();
    }
  }, [canAccessContactZ, id, isEditing]);

  useEffect(() => {
    if (!isContactZAIHandoffScript || !contactZAIHandoffConfig.connectionId || !canAccessContactZ) {
      setContactZPersonas([]);
      setContactZPersonasError('');
      return;
    }
    void fetchPersonasRef.current(contactZAIHandoffConfig.connectionId);
  }, [canAccessContactZ, contactZAIHandoffConfig.connectionId, isContactZAIHandoffScript]);

  useEffect(() => {
    const connectionId = isContactZAIHandoffScript
      ? contactZAIHandoffConfig.connectionId
      : contactZMessageConfig.connectionId;
    const needsTemplates = (
      isContactZMessageScript && contactZMessageConfig.messageType === 'official_template'
    ) || (
      isContactZAIHandoffScript
      && contactZAIHandoffConfig.initialMessage.mode === 'provided'
      && contactZHandoffProvidedType === 'template'
    );
    if (!needsTemplates || !connectionId || !canAccessContactZ) {
      setContactZOfficialTemplates([]);
      setContactZOfficialTemplatesError('');
      return;
    }
    void fetchOfficialTemplatesRef.current(connectionId);
  }, [
    canAccessContactZ,
    contactZAIHandoffConfig.connectionId,
    contactZAIHandoffConfig.initialMessage.mode,
    contactZHandoffProvidedType,
    contactZMessageConfig.connectionId,
    contactZMessageConfig.messageType,
    isContactZAIHandoffScript,
    isContactZMessageScript,
  ]);

  async function fetchScript() {
    try {
      const response = await api.get(`/api/scripts/${id}/`);
      const script = response.data;
      setName(script.name);
      setDescription(script.description || '');
      setEventType(script.eventType);
      const nextScriptType: ScriptType = script.scriptType || 'code';
      setScriptType(nextScriptType);
      setCode(nextScriptType === 'code' ? (script.code || defaultCode) : '');
      setDelaySeconds(script.delaySeconds || 0);
      setTimeoutSeconds(script.timeoutSeconds || 30);
      setPreCallMode(script.preCallPolicy?.mode || 'enrichment_only');
      setPreCallFailurePolicy(script.preCallPolicy?.failurePolicy || 'proceed');
      setPreCallMaxAttempts(script.preCallPolicy?.maxAttempts ?? 3);
      setPreCallInitialRetrySeconds(script.preCallPolicy?.initialRetrySeconds ?? 30);
      setPreCallMaxRetrySeconds(script.preCallPolicy?.maxRetrySeconds ?? 900);
      setPreCallBreakerFailureThreshold(script.preCallPolicy?.breakerFailureThreshold ?? 3);
      setPreCallBreakerOpenSeconds(script.preCallPolicy?.breakerOpenSeconds ?? 60);
      setPreCallMaxFreshnessSeconds(script.preCallPolicy?.maxFreshnessSeconds ?? 60);
      setPreCallMaxEnrichmentVariables(script.preCallPolicy?.maxEnrichmentVariables ?? 8);
      setPreCallMaxEnrichmentValueBytes(script.preCallPolicy?.maxEnrichmentValueBytes ?? 512);
      setContactZMessageConfig({
        ...buildDefaultContactZConfig(),
        ...(script.contactZMessageConfig || {}),
      });
      setContactZAIHandoffConfig({
        ...buildDefaultContactZAIHandoffConfig(),
        ...(script.contactZAIHandoffConfig || {}),
        initialMessage: {
          ...buildDefaultContactZAIHandoffConfig().initialMessage,
          ...(script.contactZAIHandoffConfig?.initialMessage || {}),
        },
      });
      setContactZHandoffProvidedType(
        script.contactZAIHandoffConfig?.initialMessage?.templateName ? 'template' : 'text',
      );
    } catch (fetchError) {
      console.error('Error loading script:', fetchError);
      setError(t('pages:scripts.errors.loading'));
    }
  }

  async function fetchAssets() {
    if (!id) return;
    try {
      const response = await api.get(`/api/scripts/${id}/assets/`);
      setAssets(Array.isArray(response.data) ? response.data : []);
    } catch (fetchError) {
      console.error('Error loading script assets:', fetchError);
      setAssets([]);
    }
  }

  async function fetchConnections() {
    try {
      setContactZConnectionsLoading(true);
      setContactZConnectionsError('');
      const connections = await fetchContactZConnections();
      const refreshedConnections = await Promise.all(connections.map(async (connection) => {
        try {
          const refreshedConnection = await getContactZConnectionState(connection.id);
          return refreshedConnection || connection;
        } catch (stateError) {
          console.warn('Error refreshing Contact Z connection contract:', stateError);
          return connection;
        }
      }));
      setContactZConnections(refreshedConnections);
    } catch (fetchError) {
      console.error('Error loading Contact Z connections:', fetchError);
      setContactZConnections([]);
      setContactZConnectionsError(t('pages:scripts.contactZ.errors.loadingConnections'));
    } finally {
      setContactZConnectionsLoading(false);
    }
  }

  async function fetchPersonas(connectionId: string) {
    try {
      setContactZPersonasLoading(true);
      setContactZPersonasError('');
      const personas = await fetchContactZIntelligencePersonas(connectionId);
      setContactZPersonas(personas);
    } catch (fetchError) {
      console.error('Error loading Contact Z intelligence personas:', fetchError);
      setContactZPersonas([]);
      setContactZPersonasError(t('pages:scripts.contactZAIHandoff.errors.loadingPersonas'));
    } finally {
      setContactZPersonasLoading(false);
    }
  }

  async function fetchOfficialTemplates(connectionId: string) {
    try {
      setContactZOfficialTemplatesLoading(true);
      setContactZOfficialTemplatesError('');
      const templates = await fetchContactZOfficialTemplates(connectionId);
      setContactZOfficialTemplates(templates);
    } catch (fetchError) {
      console.error('Error loading Contact Z official templates:', fetchError);
      setContactZOfficialTemplates([]);
      setContactZOfficialTemplatesError(
        t('pages:scripts.contactZTemplates.errors.loading'),
      );
    } finally {
      setContactZOfficialTemplatesLoading(false);
    }
  }

  async function handleSyncOfficialTemplates() {
    const connectionId = isContactZAIHandoffScript
      ? contactZAIHandoffConfig.connectionId
      : contactZMessageConfig.connectionId;
    if (!connectionId) return;
    try {
      setContactZOfficialTemplatesSyncing(true);
      setContactZOfficialTemplatesError('');
      await syncContactZOfficialTemplates(connectionId);
      await fetchOfficialTemplates(connectionId);
    } catch (syncError) {
      console.error('Error syncing Contact Z official templates:', syncError);
      setContactZOfficialTemplatesError(
        t('pages:scripts.contactZTemplates.errors.syncing'),
      );
    } finally {
      setContactZOfficialTemplatesSyncing(false);
    }
  }

  fetchScriptRef.current = fetchScript;
  fetchAssetsRef.current = fetchAssets;
  fetchConnectionsRef.current = fetchConnections;
  fetchPersonasRef.current = fetchPersonas;
  fetchOfficialTemplatesRef.current = fetchOfficialTemplates;

  function formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${formatNumber(bytes)} B`;
  }

  function handleAssetFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] || null;
    setAssetError('');
    setAssetSuccess('');
    setAssetFile(file);
  }

  async function handleUploadAsset() {
    if (!id) return;
    if (!assetFile) {
      setAssetError(t('pages:scripts.assets.errors.fileRequired'));
      return;
    }
    if (assetFile.size > SCRIPT_ASSET_TECHNICAL_MAX_BYTES) {
      setAssetError(t('pages:scripts.assets.errors.fileTooLarge'));
      return;
    }

    setAssetLoading(true);
    setAssetError('');
    setAssetSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', assetFile);
      if (assetName.trim()) {
        formData.append('name', assetName.trim());
      }
      await api.post(`/api/scripts/${id}/assets/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAssetName('');
      setAssetFile(null);
      setAssetSuccess(t('pages:scripts.assets.uploadSuccess'));
      await fetchAssets();
    } catch (uploadError) {
      setAssetError(
        resolveScriptErrorMessage(uploadError, t('pages:scripts.assets.errors.uploadFailed')),
      );
    } finally {
      setAssetLoading(false);
    }
  }

  async function handleDeleteAsset(assetId: string) {
    if (!id) return;
    setAssetError('');
    setAssetSuccess('');
    try {
      await api.delete(`/api/scripts/${id}/assets/${assetId}/`);
      if (contactZMessageConfig.assetId === assetId) {
        setContactZMessageConfig((previous) => ({ ...previous, assetId: null }));
      }
      setAssetSuccess(t('pages:scripts.assets.deleteSuccess'));
      await fetchAssets();
    } catch (deleteError) {
      console.error('Error deleting script asset:', deleteError);
      setAssetError(t('pages:scripts.assets.errors.deleteFailed'));
    }
  }

  async function handleCopy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setAssetSuccess(t('pages:scripts.assets.copySuccess'));
    } catch (copyError) {
      console.error('Error copying script asset public id:', copyError);
      setAssetError(t('pages:scripts.assets.errors.copyFailed'));
    }
  }

  function handleChangeContactZField<Key extends keyof ContactZMessageConfig>(
    field: Key,
    value: ContactZMessageConfig[Key],
  ) {
    setContactZMessageConfig((previous) => ({
      ...previous,
      [field]: value,
    }));
  }

  function handleChangeContactZAIHandoffField<Key extends keyof ContactZAIHandoffConfig>(
    field: Key,
    value: ContactZAIHandoffConfig[Key],
  ) {
    setContactZAIHandoffConfig((previous) => {
      const nextConfig = {
        ...previous,
        [field]: value,
      };
      if (field === 'connectionId') {
        nextConfig.intelligencePersonaUuid = '';
      }
      return nextConfig;
    });
  }

  function handleChangeContactZMessageType(nextMessageType: ContactZMessageType) {
    setContactZMessageConfig((previous) => ({
      ...previous,
      messageType: nextMessageType,
      textTemplate: contactZMessageTypeSupportsTextTemplate(nextMessageType)
        ? previous.textTemplate || ''
        : '',
      captionTemplate: contactZMessageTypeSupportsCaption(nextMessageType)
        ? previous.captionTemplate || ''
        : '',
      assetId: contactZMessageTypeRequiresAsset(nextMessageType)
        ? previous.assetId || null
        : null,
      templateName: nextMessageType === 'official_template' ? previous.templateName || '' : '',
      templateLanguage: nextMessageType === 'official_template' ? previous.templateLanguage || 'pt_BR' : 'pt_BR',
      templateComponents: nextMessageType === 'official_template' ? previous.templateComponents || [] : [],
    }));
  }

  function handleSelectOfficialTemplate(value: string) {
    const [templateName, templateLanguage] = value.split('::');
    if (isContactZAIHandoffScript) {
      setContactZAIHandoffConfig((previous) => ({
        ...previous,
        initialMessage: {
          ...previous.initialMessage,
          templateName: templateName || '',
          templateLanguage: templateLanguage || 'pt_BR',
          templateComponents: [],
          text: '',
        },
      }));
      return;
    }
    setContactZMessageConfig((previous) => ({
      ...previous,
      templateName: templateName || '',
      templateLanguage: templateLanguage || 'pt_BR',
      templateComponents: [],
    }));
  }

  function handleChangeTemplateParameter(slot: ContactZTemplateParameterSlot, value: string) {
    if (isContactZAIHandoffScript) {
      setContactZAIHandoffConfig((previous) => ({
        ...previous,
        initialMessage: {
          ...previous.initialMessage,
          templateComponents: setContactZTemplateParameterValue(
            previous.initialMessage.templateComponents || [],
            slot,
            value,
          ),
        },
      }));
      return;
    }
    setContactZMessageConfig((previous) => ({
      ...previous,
      templateComponents: setContactZTemplateParameterValue(
        previous.templateComponents || [],
        slot,
        value,
      ),
    }));
  }

  function validateContactZConfig(): string | null {
    if (!contactZMessageConfig.connectionId) {
      return t('pages:scripts.contactZ.validation.connectionRequired');
    }
    if (!contactZMessageConfig.targetPhonePath.trim()) {
      return t('pages:scripts.contactZ.validation.targetPhonePathRequired');
    }
    if (contactZMessageConfig.messageType === 'text' && !contactZMessageConfig.textTemplate?.trim()) {
      return t('pages:scripts.contactZ.validation.textTemplateRequired');
    }
    if (contactZMessageConfig.messageType === 'official_template') {
      if (selectedContactZMessageConnection?.platform !== 'whatsapp_cloud') {
        return t('pages:scripts.contactZTemplates.validation.cloudRequired');
      }
      if (!contactZMessageConfig.templateName) {
        return t('pages:scripts.contactZTemplates.validation.templateRequired');
      }
      if (contactZTemplateParameterSlots.some((slot) => !getContactZTemplateParameterValue(
        contactZMessageConfig.templateComponents || [],
        slot,
      ).trim())) {
        return t('pages:scripts.contactZTemplates.validation.parametersRequired');
      }
    }
    if (requiresAsset && !contactZMessageConfig.assetId) {
      return t('pages:scripts.contactZ.validation.assetRequired');
    }
    return null;
  }

  function validateContactZAIHandoffConfig(): string | null {
    if (!contactZAIHandoffConfig.connectionId) {
      return t('pages:scripts.contactZAIHandoff.validation.connectionRequired');
    }
    if (!contactZAIHandoffConfig.intelligencePersonaUuid) {
      return t('pages:scripts.contactZAIHandoff.validation.personaRequired');
    }
    if (!contactZAIHandoffConfig.targetPhonePath.trim()) {
      return t('pages:scripts.contactZAIHandoff.validation.targetPhonePathRequired');
    }
    if (!selectedContactZAIHandoffConnection || !isContactZHandoffConnectionEligible(selectedContactZAIHandoffConnection)) {
      return t('pages:scripts.contactZAIHandoff.validation.channelIncompatible');
    }
    if (contactZAIHandoffConfig.initialMessage.mode === 'provided') {
      if (contactZHandoffProvidedType === 'text' && !contactZAIHandoffConfig.initialMessage.text?.trim()) {
        return t('pages:scripts.contactZAIHandoff.validation.initialTextRequired');
      }
      if (contactZHandoffProvidedType === 'template') {
        if (selectedContactZAIHandoffConnection.platform !== 'whatsapp_cloud') {
          return t('pages:scripts.contactZTemplates.validation.cloudRequired');
        }
        if (!contactZAIHandoffConfig.initialMessage.templateName) {
          return t('pages:scripts.contactZTemplates.validation.templateRequired');
        }
        if (contactZTemplateParameterSlots.some((slot) => !getContactZTemplateParameterValue(
          contactZAIHandoffConfig.initialMessage.templateComponents || [],
          slot,
        ).trim())) {
          return t('pages:scripts.contactZTemplates.validation.parametersRequired');
        }
      }
    }
    return null;
  }

  async function handlePreCallTest() {
    setPreCallTestError('');
    setPreCallTestOutput('');
    setPreCallTestResult('');
    let inputData: Record<string, unknown>;
    try {
      inputData = JSON.parse(preCallTestInput) as Record<string, unknown>;
    } catch {
      setPreCallTestError(t('pages:scripts.preCall.test.invalidJson'));
      return;
    }

    setPreCallTestLoading(true);
    try {
      const response = await api.post('/api/scripts/test/', {
        eventType: 'pre_call',
        scriptType: 'code',
        language: SCRIPT_LANGUAGE,
        code,
        timeout: timeoutSeconds,
        inputData,
        preCallPolicy: {
          mode: preCallMode,
          failurePolicy: preCallMode === 'enrichment_only' ? 'proceed' : preCallFailurePolicy,
          maxAttempts: preCallMaxAttempts,
          initialRetrySeconds: preCallInitialRetrySeconds,
          maxRetrySeconds: preCallMaxRetrySeconds,
          breakerFailureThreshold: preCallBreakerFailureThreshold,
          breakerOpenSeconds: preCallBreakerOpenSeconds,
          maxFreshnessSeconds: preCallMaxFreshnessSeconds,
          maxEnrichmentVariables: preCallMaxEnrichmentVariables,
          maxEnrichmentValueBytes: preCallMaxEnrichmentValueBytes,
        },
      });
      setPreCallTestOutput(String(response.data.output || ''));
      setPreCallTestResult(JSON.stringify(response.data.structuredResult || {}, null, 2));
    } catch (testError) {
      setPreCallTestError(
        resolveScriptErrorMessage(testError, t('pages:scripts.preCall.test.failed')),
      );
    } finally {
      setPreCallTestLoading(false);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);

    if (isPreCallScript && preCallMode === 'gate' && preCallFailurePolicy === 'proceed') {
      const confirmed = await confirm({
        title: t('pages:scripts.preCall.failOpen.title'),
        message: t('pages:scripts.preCall.failOpen.message'),
        type: 'warning',
        confirmText: t('pages:scripts.preCall.failOpen.confirm'),
        cancelText: t('common:actions.cancel'),
      });
      if (!confirmed) {
        setLoading(false);
        return;
      }
    }

    if (scriptType === 'code' && !code.trim()) {
      setError(t('pages:scripts.errors.codeRequired'));
      setLoading(false);
      return;
    }

    if (scriptType === 'contact_z_message') {
      const configError = validateContactZConfig();
      if (configError) {
        setError(configError);
        setLoading(false);
        return;
      }
    }

    if (scriptType === 'contact_z_ai_handoff') {
      const configError = validateContactZAIHandoffConfig();
      if (configError) {
        setError(configError);
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        name,
        description,
        eventType,
        scriptType,
        language: SCRIPT_LANGUAGE,
        code: scriptType === 'code' ? code : '',
        delaySeconds,
        timeoutSeconds,
        preCallPolicy: isPreCallScript
          ? {
            mode: preCallMode,
            failurePolicy: preCallMode === 'enrichment_only' ? 'proceed' : preCallFailurePolicy,
            maxAttempts: preCallMaxAttempts,
            initialRetrySeconds: preCallInitialRetrySeconds,
            maxRetrySeconds: preCallMaxRetrySeconds,
            breakerFailureThreshold: preCallBreakerFailureThreshold,
            breakerOpenSeconds: preCallBreakerOpenSeconds,
            maxFreshnessSeconds: preCallMaxFreshnessSeconds,
            maxEnrichmentVariables: preCallMaxEnrichmentVariables,
            maxEnrichmentValueBytes: preCallMaxEnrichmentValueBytes,
          }
          : undefined,
        contactZMessageConfig: scriptType === 'contact_z_message'
          ? {
            ...contactZMessageConfig,
            textTemplate: supportsTextTemplate ? (contactZMessageConfig.textTemplate || '') : '',
            captionTemplate: supportsCaption ? (contactZMessageConfig.captionTemplate || '') : '',
            assetId: requiresAsset ? contactZMessageConfig.assetId : null,
            templateName: contactZMessageConfig.messageType === 'official_template' ? contactZMessageConfig.templateName : '',
            templateLanguage: contactZMessageConfig.messageType === 'official_template' ? contactZMessageConfig.templateLanguage : 'pt_BR',
            templateComponents: contactZMessageConfig.messageType === 'official_template' ? contactZMessageConfig.templateComponents : [],
          }
          : {},
        contactZAIHandoffConfig: scriptType === 'contact_z_ai_handoff'
          ? {
            ...contactZAIHandoffConfig,
            targetPhonePath: contactZAIHandoffConfig.targetPhonePath.trim(),
            instructionsTemplate: contactZAIHandoffConfig.instructionsTemplate || '',
            initialMessage: contactZAIHandoffConfig.initialMessage,
          }
          : {},
      };

      if (isEditing) {
        await api.put(`/api/scripts/${id}/`, payload);
      } else {
        await api.post('/api/scripts/', payload);
      }
      navigate('/scripts');
    } catch (submitError) {
      setError(resolveScriptErrorMessage(submitError, t('pages:scripts.errors.saving')));
    } finally {
      setLoading(false);
    }
  }

  function handleToggleVariableSection(sectionKey: string) {
    return (_: React.SyntheticEvent, expanded: boolean) => {
      setExpandedVariableSections((previous) => ({
        ...previous,
        [sectionKey]: expanded,
      }));
    };
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/scripts')}>
          {t('pages:scripts.actions.back')}
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {isEditing ? t('pages:scripts.editTitle') : t('pages:scripts.newTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('pages:scripts.configureAutomations')}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {scriptType === 'code' && <DataObject sx={{ color: 'primary.main' }} />}
                    {scriptType === 'contact_z_message' && <Hub sx={{ color: 'primary.main' }} />}
                    {scriptType === 'contact_z_ai_handoff' && <Psychology sx={{ color: 'primary.main' }} />}
                    <Typography variant="h6">
                      {scriptType === 'code'
                        ? t('pages:scripts.code')
                        : scriptType === 'contact_z_ai_handoff'
                          ? t('pages:scripts.contactZAIHandoff.title')
                          : t('pages:scripts.contactZ.title')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setDocumentationDrawer('variables')}
                    >
                      {t('pages:scripts.availableVariables.action')}
                    </Button>
                    {scriptType === 'code' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDocumentationDrawer('libraries')}
                      >
                        {t('pages:scripts.availableLibraries.action')}
                      </Button>
                    )}
                  </Box>
                </Box>

                {scriptType === 'code' ? (
                  <>
                    <Box sx={{ border: '1px solid #2a2a3a', borderRadius: 2, overflow: 'hidden', minHeight: 460 }}>
                      <Editor
                        height="460px"
                        language="python"
                        theme="vs-dark"
                        value={code}
                        onChange={(value) => setCode(value || '')}
                        options={{
                          automaticLayout: true,
                          minimap: { enabled: false },
                          fontSize: 14,
                          fontFamily: 'JetBrains Mono',
                          padding: { top: 16 },
                        }}
                      />
                    </Box>
                    {isPreCallScript && hasPermission('scripts_execute') && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Divider />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {t('pages:scripts.preCall.test.title')}
                          </Typography>
                          <Button
                            type="button"
                            variant="outlined"
                            startIcon={<PlayArrow />}
                            onClick={() => void handlePreCallTest()}
                            disabled={preCallTestLoading}
                          >
                            {preCallTestLoading
                              ? t('pages:scripts.preCall.test.running')
                              : t('pages:scripts.preCall.test.run')}
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          label={t('pages:scripts.preCall.test.input')}
                          value={preCallTestInput}
                          onChange={(inputEvent) => setPreCallTestInput(inputEvent.target.value)}
                          inputProps={{ sx: { fontFamily: 'monospace' } }}
                        />
                        {preCallTestError && <Alert severity="error">{preCallTestError}</Alert>}
                        {(preCallTestOutput || preCallTestResult) && (
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t('pages:scripts.preCall.test.logs')}
                              </Typography>
                              <Box component="pre" sx={{ m: 0, p: 1.5, overflow: 'auto', bgcolor: 'action.hover', minHeight: 96 }}>
                                {preCallTestOutput || '-'}
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t('pages:scripts.preCall.test.structuredResult')}
                              </Typography>
                              <Box component="pre" sx={{ m: 0, p: 1.5, overflow: 'auto', bgcolor: 'action.hover', minHeight: 96 }}>
                                {preCallTestResult || '-'}
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {isContactZMessageScript && (
                      <Alert severity="info">
                        {t('pages:scripts.contactZ.description')}
                      </Alert>
                    )}
                    {isContactZAIHandoffScript && (
                      <Alert severity="info">
                        {t('pages:scripts.contactZAIHandoff.description')}
                      </Alert>
                    )}

                    {!canAccessContactZ && (
                      <Alert severity="warning">
                        {t('pages:scripts.contactZ.noAccess')}
                      </Alert>
                    )}

                    {contactZConnectionsError && (
                      <Alert severity="warning">
                        {contactZConnectionsError}
                      </Alert>
                    )}

                    {isContactZMessageScript && (
                      <>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={contactZConnectionsLoading || !canAccessContactZ}>
                              <InputLabel>{t('pages:scripts.contactZ.fields.connection')}</InputLabel>
                              <Select
                                value={contactZMessageConfig.connectionId}
                                label={t('pages:scripts.contactZ.fields.connection')}
                                onChange={(selectEvent) => handleChangeContactZField('connectionId', String(selectEvent.target.value))}
                              >
                                <MenuItem value="">
                                  <em>{t('pages:scripts.contactZ.placeholders.selectConnection')}</em>
                                </MenuItem>
                                {contactZConnections.map((connection) => (
                                  <MenuItem key={connection.id} value={connection.id}>
                                    {connection.name} · {connection.platform} · {connection.operationMode}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t('pages:scripts.contactZ.fields.messageType')}</InputLabel>
                              <Select
                                value={contactZMessageConfig.messageType}
                                label={t('pages:scripts.contactZ.fields.messageType')}
                                onChange={(selectEvent) => handleChangeContactZMessageType(selectEvent.target.value as ContactZMessageType)}
                              >
                                <MenuItem value="text">{t('pages:scripts.contactZ.messageTypes.text')}</MenuItem>
                                <MenuItem value="audio">{t('pages:scripts.contactZ.messageTypes.audio')}</MenuItem>
                                <MenuItem value="image">{t('pages:scripts.contactZ.messageTypes.image')}</MenuItem>
                                <MenuItem value="video">{t('pages:scripts.contactZ.messageTypes.video')}</MenuItem>
                                <MenuItem value="document">{t('pages:scripts.contactZ.messageTypes.document')}</MenuItem>
                                <MenuItem value="official_template">{t('pages:scripts.contactZ.messageTypes.official_template')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <TextField
                              fullWidth
                              label={t('pages:scripts.contactZ.fields.targetPhonePath')}
                              value={contactZMessageConfig.targetPhonePath}
                              onChange={(inputEvent) => handleChangeContactZField('targetPhonePath', inputEvent.target.value)}
                              helperText={t('pages:scripts.contactZ.helpers.targetPhonePath')}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={contactZMessageConfig.fallbackToCallToNumber}
                                  onChange={(inputEvent) => handleChangeContactZField('fallbackToCallToNumber', inputEvent.target.checked)}
                                />
                              )}
                              label={t('pages:scripts.contactZ.fields.fallbackToCallToNumber')}
                            />
                          </Grid>
                          {supportsTextTemplate && (
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                label={contactZMessageConfig.messageType === 'official_template'
                                  ? t('pages:scripts.contactZTemplates.fields.localPreview')
                                  : t('pages:scripts.contactZ.fields.textTemplate')}
                                value={contactZMessageConfig.textTemplate || ''}
                                onChange={(inputEvent) => handleChangeContactZField('textTemplate', inputEvent.target.value)}
                                helperText={contactZMessageConfig.messageType === 'official_template'
                                  ? t('pages:scripts.contactZTemplates.helpers.localPreview')
                                  : t('pages:scripts.contactZ.helpers.textTemplate')}
                              />
                            </Grid>
                          )}
                          {contactZMessageConfig.messageType === 'official_template' && (
                            <>
                              <Grid size={{ xs: 12, md: 9 }}>
                                <FormControl fullWidth disabled={contactZOfficialTemplatesLoading}>
                                  <InputLabel>{t('pages:scripts.contactZTemplates.fields.template')}</InputLabel>
                                  <Select
                                    value={selectedContactZOfficialTemplate && contactZMessageConfig.templateName
                                      ? `${contactZMessageConfig.templateName}::${contactZMessageConfig.templateLanguage || 'pt_BR'}`
                                      : ''}
                                    label={t('pages:scripts.contactZTemplates.fields.template')}
                                    onChange={(selectEvent) => handleSelectOfficialTemplate(String(selectEvent.target.value))}
                                  >
                                    <MenuItem value="">
                                      <em>{t('pages:scripts.contactZTemplates.placeholders.selectTemplate')}</em>
                                    </MenuItem>
                                    {contactZOfficialTemplates.map((template) => (
                                      <MenuItem key={`${template.id}:${template.language}`} value={`${template.name}::${template.language}`}>
                                        {template.name} · {template.language} · {template.category}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={{ xs: 12, md: 3 }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<Refresh />}
                                  disabled={contactZOfficialTemplatesSyncing || !contactZMessageConfig.connectionId}
                                  onClick={() => void handleSyncOfficialTemplates()}
                                  sx={{ minHeight: 56 }}
                                >
                                  {t('pages:scripts.contactZTemplates.actions.sync')}
                                </Button>
                              </Grid>
                              {contactZTemplateParameterSlots.map((slot) => (
                                <Grid key={slot.id} size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    fullWidth
                                    label={t('pages:scripts.contactZTemplates.fields.parameter', {
                                      component: slot.componentType,
                                      index: slot.parameterIndex + 1,
                                    })}
                                    value={getContactZTemplateParameterValue(
                                      contactZMessageConfig.templateComponents || [],
                                      slot,
                                    )}
                                    onChange={(inputEvent) => handleChangeTemplateParameter(slot, inputEvent.target.value)}
                                    helperText={slot.parameterType === 'text'
                                      ? t('pages:scripts.contactZTemplates.helpers.parameterText')
                                      : t('pages:scripts.contactZTemplates.helpers.parameterMedia')}
                                  />
                                </Grid>
                              ))}
                              {contactZOfficialTemplatesError && (
                                <Grid size={{ xs: 12 }}>
                                  <Alert severity="warning">{contactZOfficialTemplatesError}</Alert>
                                </Grid>
                              )}
                            </>
                          )}
                          {supportsCaption && (
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={t('pages:scripts.contactZ.fields.captionTemplate')}
                                value={contactZMessageConfig.captionTemplate || ''}
                                onChange={(inputEvent) => handleChangeContactZField('captionTemplate', inputEvent.target.value)}
                                helperText={t('pages:scripts.contactZ.helpers.captionTemplate')}
                              />
                            </Grid>
                          )}
                          {requiresAsset && (
                            <Grid size={{ xs: 12 }}>
                              <FormControl fullWidth disabled={!isEditing || assets.length === 0}>
                                <InputLabel>{t('pages:scripts.contactZ.fields.asset')}</InputLabel>
                                <Select
                                  value={contactZMessageConfig.assetId || ''}
                                  label={t('pages:scripts.contactZ.fields.asset')}
                                  onChange={(selectEvent) => handleChangeContactZField('assetId', String(selectEvent.target.value) || null)}
                                >
                                  <MenuItem value="">
                                    <em>{t('pages:scripts.contactZ.placeholders.selectAsset')}</em>
                                  </MenuItem>
                                  {assets.map((asset) => (
                                    <MenuItem key={asset.id} value={asset.id}>
                                      {asset.name || asset.filename}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip label={t(`pages:scripts.contactZ.messageTypes.${contactZMessageConfig.messageType}`)} size="small" variant="outlined" />
                          {selectedContactZMessageConnection && (
                            <Chip label={`${selectedContactZMessageConnection.name} · ${t(`pages:integrations.contactZ.status.${selectedContactZMessageConnection.status}`)}`} size="small" variant="outlined" />
                          )}
                          {messageLimitLabel && (
                            <Chip
                              label={t('pages:scripts.contactZ.helpers.limitByType', { value: messageLimitLabel })}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {selectedAsset && (
                            <Chip
                              label={`${selectedAsset.filename} · ${formatFileSize(selectedAsset.sizeBytes)}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </>
                    )}

                    {isContactZAIHandoffScript && (
                      <>
                        {contactZPersonasError && (
                          <Alert severity="warning">
                            {contactZPersonasError}
                          </Alert>
                        )}

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={contactZConnectionsLoading || !canAccessContactZ}>
                              <InputLabel>{t('pages:scripts.contactZAIHandoff.fields.connection')}</InputLabel>
                              <Select
                                value={contactZAIHandoffConfig.connectionId}
                                label={t('pages:scripts.contactZAIHandoff.fields.connection')}
                                onChange={(selectEvent) => handleChangeContactZAIHandoffField('connectionId', String(selectEvent.target.value))}
                              >
                                <MenuItem value="">
                                  <em>{t('pages:scripts.contactZAIHandoff.placeholders.selectConnection')}</em>
                                </MenuItem>
                                {contactZConnections.map((connection) => (
                                  <MenuItem
                                    key={connection.id}
                                    value={connection.id}
                                    disabled={!isContactZHandoffConnectionEligible(connection)}
                                  >
                                    {connection.name} · {connection.platform} · {connection.operationMode}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl
                              fullWidth
                              disabled={!contactZAIHandoffConfig.connectionId || contactZPersonasLoading || !canAccessContactZ}
                            >
                              <InputLabel>{t('pages:scripts.contactZAIHandoff.fields.persona')}</InputLabel>
                              <Select
                                value={contactZAIHandoffConfig.intelligencePersonaUuid}
                                label={t('pages:scripts.contactZAIHandoff.fields.persona')}
                                onChange={(selectEvent) => handleChangeContactZAIHandoffField('intelligencePersonaUuid', String(selectEvent.target.value))}
                              >
                                <MenuItem value="">
                                  <em>{t('pages:scripts.contactZAIHandoff.placeholders.selectPersona')}</em>
                                </MenuItem>
                                {contactZAIHandoffConfig.intelligencePersonaUuid && !selectedContactZPersona && (
                                  <MenuItem value={contactZAIHandoffConfig.intelligencePersonaUuid}>
                                    {contactZAIHandoffConfig.intelligencePersonaUuid}
                                  </MenuItem>
                                )}
                                {contactZPersonas.map((persona) => (
                                  <MenuItem key={persona.id} value={persona.id}>
                                    {persona.chatbot?.name ? `${persona.name} · ${persona.chatbot.name}` : persona.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <TextField
                              fullWidth
                              label={t('pages:scripts.contactZAIHandoff.fields.targetPhonePath')}
                              value={contactZAIHandoffConfig.targetPhonePath}
                              onChange={(inputEvent) => handleChangeContactZAIHandoffField('targetPhonePath', inputEvent.target.value)}
                              helperText={t('pages:scripts.contactZAIHandoff.helpers.targetPhonePath')}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={contactZAIHandoffConfig.fallbackToCallToNumber}
                                  onChange={(inputEvent) => handleChangeContactZAIHandoffField('fallbackToCallToNumber', inputEvent.target.checked)}
                                />
                              )}
                              label={t('pages:scripts.contactZAIHandoff.fields.fallbackToCallToNumber')}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={contactZAIHandoffConfig.forceAssignToAi}
                                  onChange={(inputEvent) => handleChangeContactZAIHandoffField('forceAssignToAi', inputEvent.target.checked)}
                                />
                              )}
                              label={t('pages:scripts.contactZAIHandoff.fields.forceAssignToAi')}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={4}
                              label={t('pages:scripts.contactZAIHandoff.fields.instructionsTemplate')}
                              value={contactZAIHandoffConfig.instructionsTemplate || ''}
                              onChange={(inputEvent) => handleChangeContactZAIHandoffField('instructionsTemplate', inputEvent.target.value)}
                              helperText={t('pages:scripts.contactZAIHandoff.helpers.instructionsTemplate')}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t('pages:scripts.contactZAIHandoff.fields.initialMessageMode')}</InputLabel>
                              <Select
                                value={contactZAIHandoffConfig.initialMessage.mode}
                                label={t('pages:scripts.contactZAIHandoff.fields.initialMessageMode')}
                                onChange={(selectEvent) => setContactZAIHandoffConfig((previous) => ({
                                  ...previous,
                                  initialMessage: {
                                    ...buildDefaultContactZAIHandoffConfig().initialMessage,
                                    mode: selectEvent.target.value as ContactZAIHandoffConfig['initialMessage']['mode'],
                                  },
                                }))}
                              >
                                <MenuItem value="ai_generated">{t('pages:scripts.contactZAIHandoff.initialModes.ai_generated')}</MenuItem>
                                <MenuItem value="provided">{t('pages:scripts.contactZAIHandoff.initialModes.provided')}</MenuItem>
                                <MenuItem value="none">{t('pages:scripts.contactZAIHandoff.initialModes.none')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {contactZAIHandoffConfig.initialMessage.mode === 'provided' && (
                            <Grid size={{ xs: 12, md: 6 }}>
                              <ToggleButtonGroup
                                exclusive
                                fullWidth
                                value={contactZHandoffProvidedType}
                                onChange={(_event, nextValue: 'text' | 'template' | null) => {
                                  if (!nextValue) return;
                                  setContactZHandoffProvidedType(nextValue);
                                  setContactZAIHandoffConfig((previous) => ({
                                    ...previous,
                                    initialMessage: {
                                      ...buildDefaultContactZAIHandoffConfig().initialMessage,
                                      mode: 'provided',
                                    },
                                  }));
                                }}
                              >
                                <ToggleButton value="text">{t('pages:scripts.contactZAIHandoff.providedTypes.text')}</ToggleButton>
                                <ToggleButton value="template">{t('pages:scripts.contactZAIHandoff.providedTypes.template')}</ToggleButton>
                              </ToggleButtonGroup>
                            </Grid>
                          )}
                          {contactZAIHandoffConfig.initialMessage.mode === 'provided' && contactZHandoffProvidedType === 'text' && (
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={t('pages:scripts.contactZAIHandoff.fields.initialMessageText')}
                                value={contactZAIHandoffConfig.initialMessage.text || ''}
                                onChange={(inputEvent) => setContactZAIHandoffConfig((previous) => ({
                                  ...previous,
                                  initialMessage: {
                                    ...previous.initialMessage,
                                    text: inputEvent.target.value,
                                    templateName: '',
                                    templateComponents: [],
                                  },
                                }))}
                                helperText={t('pages:scripts.contactZAIHandoff.helpers.initialMessageText')}
                              />
                            </Grid>
                          )}
                          {contactZAIHandoffConfig.initialMessage.mode === 'provided' && contactZHandoffProvidedType === 'template' && (
                            <>
                              <Grid size={{ xs: 12, md: 9 }}>
                                <FormControl fullWidth disabled={contactZOfficialTemplatesLoading}>
                                  <InputLabel>{t('pages:scripts.contactZTemplates.fields.template')}</InputLabel>
                                  <Select
                                    value={selectedContactZOfficialTemplate && contactZAIHandoffConfig.initialMessage.templateName
                                      ? `${contactZAIHandoffConfig.initialMessage.templateName}::${contactZAIHandoffConfig.initialMessage.templateLanguage || 'pt_BR'}`
                                      : ''}
                                    label={t('pages:scripts.contactZTemplates.fields.template')}
                                    onChange={(selectEvent) => handleSelectOfficialTemplate(String(selectEvent.target.value))}
                                  >
                                    <MenuItem value="">
                                      <em>{t('pages:scripts.contactZTemplates.placeholders.selectTemplate')}</em>
                                    </MenuItem>
                                    {contactZOfficialTemplates.map((template) => (
                                      <MenuItem key={`${template.id}:${template.language}`} value={`${template.name}::${template.language}`}>
                                        {template.name} · {template.language} · {template.category}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={{ xs: 12, md: 3 }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<Refresh />}
                                  disabled={contactZOfficialTemplatesSyncing || !contactZAIHandoffConfig.connectionId}
                                  onClick={() => void handleSyncOfficialTemplates()}
                                  sx={{ minHeight: 56 }}
                                >
                                  {t('pages:scripts.contactZTemplates.actions.sync')}
                                </Button>
                              </Grid>
                              {contactZTemplateParameterSlots.map((slot) => (
                                <Grid key={slot.id} size={{ xs: 12, md: 6 }}>
                                  <TextField
                                    fullWidth
                                    label={t('pages:scripts.contactZTemplates.fields.parameter', {
                                      component: slot.componentType,
                                      index: slot.parameterIndex + 1,
                                    })}
                                    value={getContactZTemplateParameterValue(
                                      contactZAIHandoffConfig.initialMessage.templateComponents || [],
                                      slot,
                                    )}
                                    onChange={(inputEvent) => handleChangeTemplateParameter(slot, inputEvent.target.value)}
                                    helperText={slot.parameterType === 'text'
                                      ? t('pages:scripts.contactZTemplates.helpers.parameterText')
                                      : t('pages:scripts.contactZTemplates.helpers.parameterMedia')}
                                  />
                                </Grid>
                              ))}
                              {contactZOfficialTemplatesError && (
                                <Grid size={{ xs: 12 }}>
                                  <Alert severity="warning">{contactZOfficialTemplatesError}</Alert>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>

                        <Alert severity="warning">
                          {t('pages:scripts.contactZAIHandoff.acceptanceNotDelivery')}
                        </Alert>

                        {contactZAIHandoffConfig.forceAssignToAi && (
                          <Alert severity="warning">
                            {t('pages:scripts.contactZAIHandoff.forceAssignAuthorization')}
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedContactZAIHandoffConnection && (
                            <Chip label={`${selectedContactZAIHandoffConnection.name} · ${t(`pages:integrations.contactZ.status.${selectedContactZAIHandoffConnection.status}`)}`} size="small" variant="outlined" />
                          )}
                          {selectedContactZPersona && (
                            <Chip label={selectedContactZPersona.name} size="small" variant="outlined" />
                          )}
                          <Chip
                            label={t('pages:scripts.contactZAIHandoff.chips.recordingRequired')}
                            size="small"
                            variant="outlined"
                          />
                        </Box>

                        {contactZAIHandoffConfig.connectionId && !contactZPersonasLoading && contactZPersonas.length === 0 && canAccessContactZ && (
                          <Alert severity="warning">
                            {t('pages:scripts.contactZAIHandoff.emptyPersonas')}
                          </Alert>
                        )}
                      </>
                    )}

                    {!contactZConnectionsLoading && contactZConnections.length === 0 && canAccessContactZ && (
                      <Alert
                        severity="info"
                        action={(
                          <Button component={RouterLink} to="/integrations/contact-z" color="inherit" size="small">
                            {t('pages:scripts.contactZ.actions.openIntegration')}
                          </Button>
                        )}
                      >
                        {t('pages:scripts.contactZ.emptyConnections')}
                      </Alert>
                    )}

                    {requiresAsset && !isEditing && (
                      <Alert severity="info">
                        {t('pages:scripts.contactZ.assetUploadRequiresSave')}
                      </Alert>
                    )}

                    {requiresAsset && isEditing && assets.length === 0 && (
                      <Alert severity="warning">
                        {t('pages:scripts.contactZ.noAssetsAvailable')}
                      </Alert>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('pages:scripts.settings')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label={t('pages:scripts.name')}
                    value={name}
                    onChange={(inputEvent) => setName(inputEvent.target.value)}
                    required
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t('pages:scripts.scriptType')}</InputLabel>
                    <Select
                      value={scriptType}
                      label={t('pages:scripts.scriptType')}
                      onChange={(selectEvent) => setScriptType(selectEvent.target.value as ScriptType)}
                      disabled={isPreCallScript}
                    >
                      <MenuItem value="code">{t('pages:scripts.scriptTypes.code')}</MenuItem>
                      <MenuItem value="contact_z_message">{t('pages:scripts.scriptTypes.contact_z_message')}</MenuItem>
                      <MenuItem value="contact_z_ai_handoff">{t('pages:scripts.scriptTypes.contact_z_ai_handoff')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>{t('pages:scripts.event')}</InputLabel>
                    <Select
                      value={eventType}
                      label={t('pages:scripts.event')}
                      onChange={(selectEvent) => {
                        const nextEventType = String(selectEvent.target.value);
                        setEventType(nextEventType);
                        if (nextEventType === 'pre_call') {
                          setScriptType('code');
                          setDelaySeconds(0);
                          setTimeoutSeconds(10);
                          setPreCallMode('enrichment_only');
                          setPreCallFailurePolicy('proceed');
                          if (!isEditing) {
                            setCode(PRE_CALL_DEFAULT_CODE);
                          }
                        }
                      }}
                    >
                      {eventTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label={t('pages:scripts.description')}
                    value={description}
                    onChange={(inputEvent) => setDescription(inputEvent.target.value)}
                    multiline
                    rows={3}
                  />
                  <TextField
                    fullWidth
                    label={t('pages:scripts.delaySeconds')}
                    type="number"
                    value={delaySeconds}
                    onChange={(inputEvent) => setDelaySeconds(Math.max(0, parseInt(inputEvent.target.value, 10) || 0))}
                    helperText={t('pages:scripts.delayHelper')}
                    inputProps={{ min: 0, max: 3600 }}
                    disabled={isPreCallScript}
                  />
                  {isPreCallScript && (
                    <>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="body2">{t('pages:scripts.preCall.mode')}</Typography>
                          <Tooltip title={t('pages:scripts.preCall.modeHelp')} arrow>
                            <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <ToggleButtonGroup
                          value={preCallMode}
                          exclusive
                          fullWidth
                          size="small"
                          onChange={(_event, nextMode: PreCallMode | null) => {
                            if (nextMode === null) return;
                            setPreCallMode(nextMode);
                            if (nextMode === 'enrichment_only') {
                              setPreCallFailurePolicy('proceed');
                            } else {
                              setPreCallFailurePolicy('retry');
                            }
                          }}
                        >
                          <ToggleButton value="enrichment_only">{t('pages:scripts.preCall.modes.enrichment_only')}</ToggleButton>
                          <ToggleButton value="gate">{t('pages:scripts.preCall.modes.gate')}</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <FormControl fullWidth disabled={preCallMode === 'enrichment_only'}>
                        <InputLabel>{t('pages:scripts.preCall.failurePolicy')}</InputLabel>
                        <Select
                          value={preCallFailurePolicy}
                          label={t('pages:scripts.preCall.failurePolicy')}
                          onChange={(selectEvent) => setPreCallFailurePolicy(selectEvent.target.value as PreCallFailurePolicy)}
                        >
                          <MenuItem value="proceed">{t('pages:scripts.preCall.failurePolicies.proceed')}</MenuItem>
                          <MenuItem value="retry">{t('pages:scripts.preCall.failurePolicies.retry')}</MenuItem>
                          <MenuItem value="skip">{t('pages:scripts.preCall.failurePolicies.skip')}</MenuItem>
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField label={t('pages:scripts.preCall.fields.maxAttempts')} type="number" value={preCallMaxAttempts} onChange={(inputEvent) => setPreCallMaxAttempts(Number(inputEvent.target.value))} inputProps={{ min: 1, max: 10 }} />
                        <TextField label={t('pages:scripts.preCall.fields.initialRetrySeconds')} type="number" value={preCallInitialRetrySeconds} onChange={(inputEvent) => setPreCallInitialRetrySeconds(Number(inputEvent.target.value))} inputProps={{ min: 5, max: 900 }} />
                        <TextField label={t('pages:scripts.preCall.fields.maxRetrySeconds')} type="number" value={preCallMaxRetrySeconds} onChange={(inputEvent) => setPreCallMaxRetrySeconds(Number(inputEvent.target.value))} inputProps={{ min: 30, max: 3600 }} />
                        <TextField label={t('pages:scripts.preCall.fields.breakerFailureThreshold')} type="number" value={preCallBreakerFailureThreshold} onChange={(inputEvent) => setPreCallBreakerFailureThreshold(Number(inputEvent.target.value))} inputProps={{ min: 2, max: 20 }} />
                        <TextField label={t('pages:scripts.preCall.fields.breakerOpenSeconds')} type="number" value={preCallBreakerOpenSeconds} onChange={(inputEvent) => setPreCallBreakerOpenSeconds(Number(inputEvent.target.value))} inputProps={{ min: 30, max: 900 }} />
                        <TextField label={t('pages:scripts.preCall.fields.maxFreshnessSeconds')} type="number" value={preCallMaxFreshnessSeconds} onChange={(inputEvent) => setPreCallMaxFreshnessSeconds(Number(inputEvent.target.value))} inputProps={{ min: 5, max: 300 }} />
                        <TextField label={t('pages:scripts.preCall.fields.maxEnrichmentVariables')} type="number" value={preCallMaxEnrichmentVariables} onChange={(inputEvent) => setPreCallMaxEnrichmentVariables(Number(inputEvent.target.value))} inputProps={{ min: 0, max: 16 }} />
                        <TextField label={t('pages:scripts.preCall.fields.maxEnrichmentValueBytes')} type="number" value={preCallMaxEnrichmentValueBytes} onChange={(inputEvent) => setPreCallMaxEnrichmentValueBytes(Number(inputEvent.target.value))} inputProps={{ min: 64, max: 2048 }} />
                      </Box>
                    </>
                  )}
                  <TextField
                    fullWidth
                    label={t('pages:scripts.timeoutSeconds')}
                    type="number"
                    value={timeoutSeconds}
                    onChange={(inputEvent) => setTimeoutSeconds(
                      Math.min(
                        isPreCallScript ? 30 : 300,
                        Math.max(1, parseInt(inputEvent.target.value, 10) || 30),
                      ),
                    )}
                    helperText={t('pages:scripts.timeoutHelper')}
                    inputProps={{ min: 1, max: isPreCallScript ? 30 : 300 }}
                  />
                </Box>

                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? t('pages:scripts.actions.saving') : t('pages:scripts.actions.save')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/scripts')}
                    fullWidth
                  >
                    {t('pages:scripts.actions.cancel')}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <Typography variant="h6">{t('pages:scripts.assets.title')}</Typography>
                  <Tooltip
                    title={t('pages:scripts.assets.helpTooltip')}
                    arrow
                  >
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('pages:scripts.assets.maxSizeHelper')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('pages:scripts.assets.contactZLimitHelper')}
                </Typography>

                {!isEditing && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('pages:scripts.assets.saveBeforeUpload')}
                  </Alert>
                )}

                {isEditing && (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      label={t('pages:scripts.assets.nameLabel')}
                      value={assetName}
                      onChange={(inputEvent) => setAssetName(inputEvent.target.value)}
                      helperText={t('pages:scripts.assets.nameHelper')}
                    />
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFile />}
                    >
                      {assetFile ? `${t('pages:scripts.assets.selectedFile')}: ${assetFile.name}` : t('pages:scripts.assets.selectFile')}
                      <input
                        type="file"
                        hidden
                        onChange={handleAssetFileChange}
                      />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => void handleUploadAsset()}
                      disabled={!assetFile || assetLoading}
                    >
                      {assetLoading ? t('pages:scripts.assets.uploading') : t('pages:scripts.assets.upload')}
                    </Button>
                  </Box>
                )}

                {assetError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {assetError}
                  </Alert>
                )}
                {assetSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {assetSuccess}
                  </Alert>
                )}

                {isEditing && (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {assets.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:scripts.assets.empty')}
                      </Typography>
                    )}
                    {assets.map((asset) => {
                      const isSelectedAsset = contactZMessageConfig.assetId === asset.id;
                      return (
                        <Box
                          key={asset.id}
                          sx={{
                            border: '1px solid',
                            borderColor: isSelectedAsset ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            p: 1.5,
                          }}
                        >
                          <Typography variant="subtitle2">{asset.name || asset.filename}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {asset.filename}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {asset.mimeType || 'application/octet-stream'} • {formatFileSize(asset.sizeBytes)}
                          </Typography>
                          {isSelectedAsset && (
                            <Chip
                              label={t('pages:scripts.contactZ.selectedAsset')}
                              size="small"
                              color="primary"
                              sx={{ mt: 1 }}
                            />
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', color: 'text.secondary', wordBreak: 'break-all' }}
                            >
                              {asset.id}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {requiresAsset && (
                                <Tooltip title={t('common:actions.select')} arrow>
                                  <IconButton size="small" onClick={() => handleChangeContactZField('assetId', asset.id)}>
                                    <Hub fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={t('pages:scripts.assets.copyPublicId')} arrow>
                                <IconButton size="small" onClick={() => void handleCopy(asset.id)}>
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('pages:scripts.assets.deleteAsset')} arrow>
                                <IconButton size="small" color="error" onClick={() => void handleDeleteAsset(asset.id)}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      <Drawer
        anchor="right"
        open={Boolean(documentationDrawer)}
        onClose={() => setDocumentationDrawer(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DataObject sx={{ color: 'primary.main' }} />
              <Typography variant="h6">
                {documentationDrawer === 'variables'
                  ? t('pages:scripts.availableVariables.title')
                  : t('pages:scripts.availableLibraries.title')}
              </Typography>
            </Box>
            <IconButton onClick={() => setDocumentationDrawer(null)}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {documentationDrawer === 'variables'
              ? t('pages:scripts.availableVariables.description')
              : t('pages:scripts.availableLibraries.description')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2.5 }}>
          {documentationDrawer === 'variables' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {visiblePayloadSections.map((section) => (
                <Accordion
                  key={section.key}
                  disableGutters
                  elevation={0}
                  expanded={expandedVariableSections[section.key] !== false}
                  onChange={handleToggleVariableSection(section.key)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 1.5, py: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {section.icon}
                      <Typography variant="subtitle1" fontWeight={600}>
                        {section.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {section.fields.map((field) => (
                        <Box key={field.path}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-all' }}
                          >
                            {field.path}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {field.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {documentationDrawer === 'libraries' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Alert severity="info">
                {t('pages:scripts.availableLibraries.requestsAliasNote')}
              </Alert>
              <Alert severity="info">
                {t('pages:scripts.availableLibraries.jsonAliasNote')}
              </Alert>
              {availableLibraries.map((library) => (
                <Box
                  key={library.name}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                    {library.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {library.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>
      {ConfirmDialogComponent}
    </Box>
  );
}
